// Collector: raw SQL source → type-mismatch markers.
//
// Reuses the wasm-free single-pass parse (`buildParsedDocumentFromSource` +
// `getDocumentStructure`) and the canonical predicate extractor
// (`extractQueryStructure`) so the comparison shapes are byte-identical to what
// VQE and the operator suggester consume. Walks every query-shaped statement
// AND its nested derived-table subqueries / compound branches, resolving each
// (sub)query's column operands against ITS own in-scope relations.
//
// Pure given (source, engine, snapshotTables) — no Monaco, no I/O. The
// SqlMonaco layer supplies `snapshot.tables` and converts the returned offsets
// to Monaco positions.

import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '@/features/sql-grammar/document';
import type { SqlEngine } from '@/features/sql-grammar/parseOptions';
import type {
  Comparison,
  FromItem,
  ParsedBuffer,
  QueryStructure,
} from '@/features/sql-resolver/types';
import { extractQueryStructure } from '@/features/sql-resolver/structure/extractQueryStructure';

import { buildTypeCheckDiagnostics } from './buildTypeCheckDiagnostics';
import {
  makeColumnTypeResolver,
  type SnapshotTableLike,
} from './columnTypeFromSnapshot';
import { resolveComparisonTypes } from './resolveComparisonTypes';
import type { TypeCheckMarker } from './types';

const QUERY_KINDS = new Set(['select', 'insert', 'update', 'delete', 'merge']);

const foldName = (s: string): string => s.toLowerCase();

function foldingForEngine(engine: SqlEngine): 'lower' | 'upper' | 'case-insensitive' {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

export type CollectTypeCheckInput = {
  readonly source: string;
  readonly engine: SqlEngine;
  readonly snapshotTables: ReadonlyArray<SnapshotTableLike>;
};

export function collectTypeCheckMarkers(input: CollectTypeCheckInput): TypeCheckMarker[] {
  const { source, engine, snapshotTables } = input;
  if (!source.trim() || snapshotTables.length === 0) return [];

  const buffer = parseBuffer(source, engine);
  if (!buffer) return [];

  const out: TypeCheckMarker[] = [];
  for (const stmt of buffer.documentStructure.statements) {
    if (!QUERY_KINDS.has(stmt.kind)) continue;
    let structure: QueryStructure;
    try {
      structure = extractQueryStructure(buffer, stmt);
    } catch {
      continue; // defensive — never let one bad statement break the pass
    }
    processStructure(structure, snapshotTables, engine, out);
  }
  return out;
}

function parseBuffer(source: string, engine: SqlEngine): ParsedBuffer | null {
  try {
    const options = { engine, identifierFolding: foldingForEngine(engine) } as const;
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'type-check');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}

// Resolve + produce for one (sub)query scope, then recurse into nested ones.
function processStructure(
  qs: QueryStructure,
  snapshotTables: ReadonlyArray<SnapshotTableLike>,
  engine: SqlEngine,
  out: TypeCheckMarker[],
): void {
  const comparisons = collectComparisons(qs);
  if (comparisons.length > 0) {
    const relationNames = collectRelationNames(qs);
    const resolveColumnType = makeColumnTypeResolver(relationNames, snapshotTables);
    const resolved = comparisons.map((c) => resolveComparisonTypes(c, resolveColumnType));
    out.push(...buildTypeCheckDiagnostics(resolved, engine));
  }
  for (const nested of nestedStructures(qs)) {
    processStructure(nested, snapshotTables, engine, out);
  }
}

// Every BooleanFragment in this scope that the parser recognised as a binary
// comparison. WHERE / HAVING / JOIN ON / ON CONFLICT WHERE / MERGE ON.
function collectComparisons(qs: QueryStructure): Comparison[] {
  const fragments = [
    ...(qs.where ?? []),
    ...(qs.having ?? []),
    ...(qs.mergeOn ?? []),
    ...(qs.onConflict?.where ?? []),
    ...(qs.joins ?? []).flatMap((j) => j.on ?? []),
  ];
  return fragments.map((f) => f.comparison).filter((c): c is Comparison => c !== undefined);
}

// Folded bare names of every base relation visible in this scope: FROM items,
// JOIN right sides, and DML targets. Derived tables / CTE refs contribute no
// snapshot name (resolved via recursion / left unknown).
function collectRelationNames(qs: QueryStructure): Set<string> {
  const names = new Set<string>();
  const add = (item: FromItem | undefined): void => {
    if (item && item.target.kind === 'relation') names.add(foldName(item.target.qualified.name));
  };
  for (const fi of qs.fromItems ?? []) add(fi);
  for (const j of qs.joins ?? []) add(j.right);
  add(qs.updateTarget);
  add(qs.deleteTarget);
  add(qs.mergeTarget);
  add(qs.mergeSource);
  add(qs.insertTarget);
  return names;
}

// Nested query scopes: derived-table subqueries (FROM + JOIN), and compound
// (UNION/INTERSECT/EXCEPT) branches. Each is its own resolution scope.
function nestedStructures(qs: QueryStructure): QueryStructure[] {
  const nested: QueryStructure[] = [];
  const fromItem = (item: FromItem | undefined): void => {
    if (item && item.target.kind === 'derived-table') nested.push(item.target.subquery);
  };
  for (const fi of qs.fromItems ?? []) fromItem(fi);
  for (const j of qs.joins ?? []) fromItem(j.right);
  for (const branch of qs.compoundBranches ?? []) nested.push(branch);
  return nested;
}
